import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useQuery,
  getUserArticleLikes,
  setArticleLike,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { routes } from "wasp/client/router";

interface ArticleEngagementContextType {
  isLiked: (articleId: number) => boolean;
  getLikeCount: (articleId: number, initialLikeCount?: number) => number;
  seedLikeCount: (articleId: number, likeCount: number) => void;
  toggleLike: (articleId: number, initialLikeCount?: number) => Promise<void>;
  isLikePending: (articleId: number) => boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const ArticleEngagementContext = createContext<
  ArticleEngagementContextType | undefined
>(undefined);

export function ArticleEngagementProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: user, isLoading: authLoading } = useAuth();
  const { data: likedArticleIds, isLoading: likesLoading } = useQuery(
    getUserArticleLikes,
    undefined,
    { enabled: !!user },
  );
  const [likedIds, setLikedIds] = useState<Set<number>>(new Set());
  const [pendingLikeIds, setPendingLikeIds] = useState<Set<number>>(new Set());
  const [likeCounts, setLikeCounts] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    if (!user) {
      setLikedIds(new Set());
      setPendingLikeIds(new Set());
      return;
    }

    setLikedIds(new Set(likedArticleIds ?? []));
  }, [likedArticleIds, user]);

  const seedLikeCount = useCallback((articleId: number, likeCount: number) => {
    setLikeCounts((prev) => {
      if (prev.has(articleId)) {
        return prev;
      }

      const next = new Map(prev);
      next.set(articleId, Math.max(0, likeCount));
      return next;
    });
  }, []);

  const toggleLike = useCallback(
    async (articleId: number, initialLikeCount: number = 0) => {
      if (!user) {
        window.location.href = routes.LoginRoute.to;
        return;
      }

      if (pendingLikeIds.has(articleId)) return;

      const currentlyLiked = likedIds.has(articleId);
      const nextLiked = !currentlyLiked;
      const previousLikeCount =
        likeCounts.get(articleId) ?? Math.max(0, initialLikeCount);
      const nextLikeCount = Math.max(
        0,
        previousLikeCount + (nextLiked ? 1 : -1),
      );

      setPendingLikeIds((prev) => {
        const next = new Set(prev);
        next.add(articleId);
        return next;
      });

      setLikedIds((prev) => {
        const next = new Set(prev);
        if (nextLiked) {
          next.add(articleId);
        } else {
          next.delete(articleId);
        }
        return next;
      });

      setLikeCounts((prev) => {
        const next = new Map(prev);
        next.set(articleId, nextLikeCount);
        return next;
      });

      try {
        await setArticleLike({ articleId, liked: nextLiked });
      } catch (error) {
        setLikedIds((prev) => {
          const next = new Set(prev);
          if (currentlyLiked) {
            next.add(articleId);
          } else {
            next.delete(articleId);
          }
          return next;
        });

        setLikeCounts((prev) => {
          const next = new Map(prev);
          next.set(articleId, previousLikeCount);
          return next;
        });
        throw error;
      } finally {
        setPendingLikeIds((prev) => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
      }
    },
    [likedIds, likeCounts, pendingLikeIds, user],
  );

  const value = useMemo(
    () => ({
      isLiked: (articleId: number) => likedIds.has(articleId),
      getLikeCount: (articleId: number, initialLikeCount: number = 0) =>
        likeCounts.get(articleId) ?? Math.max(0, initialLikeCount),
      seedLikeCount,
      toggleLike,
      isLikePending: (articleId: number) => pendingLikeIds.has(articleId),
      isAuthenticated: !!user,
      isLoading: authLoading || likesLoading,
    }),
    [
      authLoading,
      likeCounts,
      likesLoading,
      likedIds,
      pendingLikeIds,
      seedLikeCount,
      toggleLike,
      user,
    ],
  );

  return (
    <ArticleEngagementContext.Provider value={value}>
      {children}
    </ArticleEngagementContext.Provider>
  );
}

export function useArticleEngagement() {
  const context = useContext(ArticleEngagementContext);
  if (!context) {
    throw new Error(
      "useArticleEngagement must be used within an ArticleEngagementProvider",
    );
  }
  return context;
}
